import { Injectable } from '@angular/core';
import { Workbook } from 'exceljs';
import * as fs from 'file-saver';
import * as XLSX from 'xlsx';
// import * as logo from './mylogo.js';

@Injectable({
  providedIn: 'root'
})
export class ExportExcelService {

  private readonly headerFillColor = '4472C4'; // Blue
  private readonly headerFontColor = 'FFFFFF'; // White
  private readonly displayOnlyHeaderFillColor = 'FFE69C'; // Amber
  private readonly displayOnlyHeaderFontColor = '664D03';
  private readonly displayOnlyCellFillColor = 'FFF8E1';
  private readonly displayOnlyCellFontColor = '856404';
  private readonly displayOnlyNoticeFillColor = 'FFF3CD';
  private readonly displayOnlyNoticeFontColor = '664D03';
  private readonly totalRowFillColor = 'D4EDDA';
  private readonly totalRowFontColor = '155724';
  private readonly defaultDataRowHeight = 15;
  private readonly compactHeaderRowHeight = 36;
  private readonly noticeRowHeight = 48;

  constructor() { }

  private columnNumberToLetter(col: number): string {
    let letter = '';
    let n = col;
    while (n > 0) {
      const mod = (n - 1) % 26;
      letter = String.fromCharCode(65 + mod) + letter;
      n = Math.floor((n - 1) / 26);
    }
    return letter;
  }

  exportFromHtmlTable(tableElement: HTMLElement, title: string): void {
    const worksheet = XLSX.utils.table_to_sheet(tableElement);
    const sheetData = XLSX.utils.sheet_to_json(worksheet, { header: 1, defval: '' }) as unknown[][];
    if (!sheetData.length) {
      return;
    }

    const headers = sheetData[0].map(cell => String(cell ?? ''));
    const data = sheetData.slice(1);
    this.exportExcel({ title, headers, data });
  }

  exportFromJsonRows(rows: object[], title: string): void {
    if (!rows?.length) {
      return;
    }

    const headers = Object.keys(rows[0]);
    const data = rows.map(row => headers.map(header => (row as Record<string, unknown>)[header] ?? ''));
    this.exportExcel({ title, headers, data });
  }

  exportHeaderOnlyTemplate(title: string, columnsResponse: object[]): void {
    if (!columnsResponse?.length) {
      return;
    }
    this.exportExcel({
      title,
      headers: Object.keys(columnsResponse[0]),
      data: []
    });
  }

  exportExcel(excelData) {

    //Title, Header & Data
    const title = excelData.title;
    const header = excelData.headers;
    const data = excelData.data;
    const headerRows = excelData.headerRows || [];
    const displayOnlyColumnNumbers: number[] = excelData.displayOnlyColumnNumbers || [];

    //Create a workbook with a worksheet
    let workbook = new Workbook();
    let worksheet = workbook.addWorksheet('Sheet1');
    worksheet.properties.defaultRowHeight = this.defaultDataRowHeight;

    const applyDisplayOnlyCellStyle = (cell: any, bold = false) => {
      cell.fill = {
        type: 'pattern',
        pattern: 'solid',
        fgColor: { argb: this.displayOnlyCellFillColor },
        bgColor: { argb: '' }
      };
      cell.font = {
        bold,
        color: { argb: this.displayOnlyCellFontColor },
        size: bold ? 12 : 11
      };
      cell.alignment = {
        vertical: 'middle',
        wrapText: false
      };
    };

    const applyDisplayOnlyHeaderStyle = (cell: any) => {
      cell.fill = {
        type: 'pattern',
        pattern: 'solid',
        fgColor: { argb: this.displayOnlyHeaderFillColor },
        bgColor: { argb: '' }
      };
      cell.font = {
        bold: true,
        color: { argb: this.displayOnlyHeaderFontColor },
        size: 11
      };
      cell.alignment = {
        wrapText: true,
        vertical: 'middle',
        horizontal: 'center'
      };
    };

    // Add optional header rows if provided (e.g., Payroll Month, Sent From).
    // These meta rows are excluded from column auto-width so long notices do not stretch columns.
    if (headerRows && headerRows.length > 0) {
      headerRows.forEach(headerRowData => {
        const isHighlightNotice = !!headerRowData.highlight;
        const mergeSpan = headerRowData.mergeSpan || 0;

        if (isHighlightNotice) {
          // Single merged band across the sheet — keeps notice text out of per-column width calc
          const noticeText = [headerRowData.label, headerRowData.value]
            .filter(part => part != null && String(part).trim() !== '')
            .join(' — ');
          const row = worksheet.addRow([noticeText]);
          row.height = this.noticeRowHeight;
          const noticeCell = row.getCell(1);
          noticeCell.font = {
            bold: true,
            size: 11,
            color: { argb: this.displayOnlyNoticeFontColor }
          };
          noticeCell.fill = {
            type: 'pattern',
            pattern: 'solid',
            fgColor: { argb: this.displayOnlyNoticeFillColor }
          };
          noticeCell.alignment = { wrapText: true, vertical: 'top' };
          if (mergeSpan > 1) {
            worksheet.mergeCells(row.number, 1, row.number, mergeSpan);
          }
        } else {
          const row = worksheet.addRow([headerRowData.label, headerRowData.value]);
          row.height = this.defaultDataRowHeight;
          row.getCell(1).font = { bold: true, size: 11, color: { argb: '000000' } };
          row.getCell(2).font = { bold: false, size: 11, color: { argb: '000000' } };
          row.getCell(1).fill = {
            type: 'pattern',
            pattern: 'solid',
            fgColor: { argb: 'E7E6E6' }
          };
          row.getCell(2).fill = {
            type: 'pattern',
            pattern: 'solid',
            fgColor: { argb: 'FFFFFF' }
          };
        }
      });
      const spacerRow = worksheet.addRow([]);
      spacerRow.height = this.defaultDataRowHeight;
    }

    //Adding Column Header Row
    let headerRow = worksheet.addRow(header);
    headerRow.height = displayOnlyColumnNumbers.length > 0
      ? this.compactHeaderRowHeight
      : this.defaultDataRowHeight;
    headerRow.eachCell((cell, number) => {
      if (displayOnlyColumnNumbers.includes(number)) {
        applyDisplayOnlyHeaderStyle(cell);
        return;
      }

      cell.fill = {
        type: 'pattern',
        pattern: 'solid',
        fgColor: { argb: this.headerFillColor },
        bgColor: { argb: '' }
      };
      cell.font = {
        bold: true,
        color: { argb: this.headerFontColor },
        size: 12
      };
      cell.alignment = {
        vertical: 'middle',
        horizontal: 'center',
        wrapText: false
      };
    });

    const columnFormats: string[] = excelData.columnFormats || [];
    const freezeColumnCount = excelData.freezeColumnCount || 0;
    if (freezeColumnCount > 0) {
      const scrollColLetter = this.columnNumberToLetter(freezeColumnCount + 1);
      const scrollRow = headerRow.number + 1;
      worksheet.views = [{
        state: 'frozen',
        xSplit: freezeColumnCount,
        ySplit: headerRow.number,
        topLeftCell: `${scrollColLetter}${scrollRow}`,
        activeCell: `${scrollColLetter}${scrollRow}`
      }];
    }

    const applyColumnFormat = (cell: any, colNumber: number) => {
      if (typeof cell.value === 'string' && /^0\d+$/.test(cell.value)) {
        cell.numFmt = '@';
        return;
      }
      if (!excelData.applyCommissionFormatting || colNumber > columnFormats.length) {
        return;
      }
      const format = columnFormats[colNumber - 1];
      if (format === 'currency' && (typeof cell.value === 'number' || cell.value === '')) {
        cell.numFmt = '$#,##0.00';
      } else if (format === 'integer' && typeof cell.value === 'number') {
        cell.numFmt = '#,##0';
      } else if (format === 'percent' && typeof cell.value === 'number') {
        cell.numFmt = '0.00%';
      }
    };

    // Adding Data with Conditional Formatting
    data.forEach((d, index) => {
      let row = worksheet.addRow(d);
      row.height = this.defaultDataRowHeight;

      row.eachCell((cell, colNumber) => {
        if (!cell.alignment) {
          cell.alignment = { vertical: 'middle', wrapText: false };
        }
        if (displayOnlyColumnNumbers.includes(colNumber)) {
          applyDisplayOnlyCellStyle(cell, index === data.length - 1 && excelData.highlightLastRow);
        }
        applyColumnFormat(cell, colNumber);
      });
      
      // Highlight last row if flag is set (for grand total row)
      if (excelData.highlightLastRow && index === data.length - 1) {
        row.eachCell((cell, number) => {
          if (displayOnlyColumnNumbers.includes(number)) {
            applyDisplayOnlyCellStyle(cell, true);
            cell.border = {
              top: { style: 'thick', color: { argb: 'FFC107' } }
            };
            return;
          }

          cell.fill = {
            type: 'pattern',
            pattern: 'solid',
            fgColor: { argb: this.totalRowFillColor },
            bgColor: { argb: '' }
          };
          cell.font = {
            bold: true,
            size: 12,
            color: { argb: this.totalRowFontColor }
          };
          cell.alignment = {
            vertical: 'middle',
            wrapText: false
          };
          cell.border = {
            top: { style: 'thick', color: { argb: '28A745' } }
          };
          applyColumnFormat(cell, number);
        });
      }
    }
    );
    // Size columns from table header + data only (ignore payroll/notice meta rows above).
    const tableStartRow = headerRow.number;
    const isCommissionReport = !!excelData.applyCommissionFormatting;
    const explicitWidths: number[] = excelData.columnWidths || [];
    const maxAutoWidth = isCommissionReport ? 28 : 42;
    const minAutoWidth = isCommissionReport ? 6 : 10;

    worksheet.columns.forEach((column, colIndex) => {
      if (explicitWidths[colIndex] != null && explicitWidths[colIndex] > 0) {
        column.width = explicitWidths[colIndex];
        return;
      }

      let maxLength = 0;
      // exceljs typings expose Column as Partial<Column>; bracket access matches prior pattern
      column['eachCell']({ includeEmpty: false }, (cell) => {
        if (cell.row < tableStartRow) {
          return;
        }
        const value = cell.value == null ? '' : String(cell.value);
        const longestLine = value.split('\n').reduce((max, line) => Math.max(max, line.length), 0);
        if (longestLine > maxLength) {
          maxLength = longestLine;
        }
      });
      column.width = Math.min(Math.max(maxLength + 2, minAutoWidth), maxAutoWidth);
    });

    //Merge Cells
    //worksheet.mergeCells(`A${footerRow.number}:F${footerRow.number}`);
    //Generate & Save Excel File
    workbook.xlsx.writeBuffer().then((data) => {
      let blob = new Blob([data], { type: 'application/octet-stream' });
      fs.saveAs(blob, title + '.xlsx');
    })

  }
}